import {
  ChevronDownRegularIcon,
  HouseMedicalChimneyRegularIcon,
} from "@elixir/icons";
import {
  APPOINTMENT_CHANNEL,
  Appointment,
  AptChannel,
  BookType,
} from "@elixir/types";
import { isSameDay } from "@elixir/utils";
import { Button, Radio, notification } from "antd";
import generatePicker from "antd/es/date-picker/generatePicker";
import dateFormat from "dateformat";
import dayjs, { Dayjs } from "dayjs";
import dayjsGenerateConfig from "rc-picker/lib/generate/dayjs";
import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { InputField, PastVisitSlotPlaceholderUi, PrimaryButton } from "ui";
import { shallow } from "zustand/shallow";
import {
  getCustomSlots,
  useCustomSlots,
} from "../../../apis/tryst/get-custom-slots";
import { Select } from "antd";
import { getSlotsPref } from "../../../utils/get-calendar-slot-mode";
import usePostBookSlot from "../../../apis/tryst/post-book-slot";
import usePostRescheduleAppointment from "../../../apis/tryst/post-reschedule-appointment";
import useScheduleFollowupMutation from "../../../apis/tryst/schedule-followup-appointment";
import useScheduleRequestedAppointment from "../../../apis/tryst/schedule-requested-appointment";
import { ReactComponent as CalendarSolidIcon } from "../../../assets/icons/font-awesome/calendar-solid.svg";
import { ReactComponent as MoonSolidIcon } from "../../../assets/icons/font-awesome/moon-solid.svg";
import { ReactComponent as SunSolidIcon } from "../../../assets/icons/font-awesome/sun-solid.svg";
import { ReactComponent as SunriseSolidIcon } from "../../../assets/icons/font-awesome/sunrise-solid.svg";
import SelectSlotChip from "../../../atoms/chip/select-slot-chip";
import PopoverContainer from "../../../atoms/popover/container";
import SelectMode from "../../../organisms/queue/add-new-patient/select-mode";
import useQueueStore from "../../../stores/queue-store";
import { APPOINTMENT_STATUS } from "../../../stores/queue-store/enums";
import { TimeOfDayForSlots } from "../../../stores/queue-store/types";
import { useDoctorStaffCheck } from "../../../utils/doctor-staff-check";
import getTimeOfDay, {
  getTimeOfDaysForDate,
} from "../../../utils/get-time-of-day";
import { track } from "../../../utils/tracking";
import VisitDuration from "./visit-duration";
import { useMyAccessProfileV2 } from "../../../apis/tryst/get-my-access-profile";
import SelectDoctorForSlot from "./slot-doctor-selector";
import useGetQueueConfig from "../../../apis/tryst/get-queue-config";
import { useCreateAptStore } from "@elixir/stores";
import { getBookedAptChannel } from "../../../utils/appointment-utils";
import Spinner from "../../../atoms/spinner";
import { getMe } from "../../../utils/bridges/methods";

/**
 * The use of flex here instead of dr-flex is intentionals
 * Will be changing it soon with class specificity
 */

const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);

const durationsInMinutes = [5, 10, 15, 20, 25, 30, 45, 60];

function formatServiceDuration(name: string, minutes: number): string {
  if (minutes < 60) return `${name} (${minutes}m)`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${name} (${h}h${m > 0 ? ` ${m}m` : ""})`;
}

const SelectSlot = ({
  aptData,
  bookType,
  aptChannel,
  onSubmitClick,
}: {
  aptData: Appointment;
  aptChannel?: AptChannel;
  bookType?: BookType;
  onSubmitClick?: () => void;
  isModal?: boolean;
}) => {
  return (
    <PopoverContainer className="dr-min-h-502 overflow-y-auto dr-h-502 dr-max-w-408 dr-w-408 dr-bg-darwin-touch-neutral-0 BodyBody2Bold dr-rounded-16  flex dr-flex-col dr-justify-between dr-relative">
      <SlotSelectionContent
        aptData={aptData}
        aptChannel={aptChannel}
        bookType={bookType}
        onSubmitClick={onSubmitClick}
      />
    </PopoverContainer>
  );
};

export default SelectSlot;

export const SlotSelectionContent = ({
  aptData,
  aptChannel,
  bookType,
  onSubmitClick,
  forceShowSlotsFromSchedule,
}: {
  aptData: Appointment;
  aptChannel?: AptChannel;
  bookType?: BookType;
  onSubmitClick?: (apt?: Pick<Appointment, "full_date"> | Appointment) => void;
  forceShowSlotsFromSchedule?: boolean;
}) => {
  if (!aptData) {
    return (
      <div className="dr-flex dr-items-center dr-justify-center dr-h-502 dr-p-16">
        <p className="BodyBody2Regular">
          <Spinner size={16} />
        </p>
      </div>
    );
  }
  const clinicId = useQueueStore((state) => state?.clinicId);
  const clinicIdFromCreateAptStore = useCreateAptStore(
    (state) => state.selectedClinic
  );
  const customSlotDate = useQueueStore((state) => state.customSlotDate);
  const selectedSlot = useQueueStore((state) => state.selectedSlot);
  const selectSlot = useQueueStore((state) => state.selectSlot);
  const clearSelectedSlot = useQueueStore((state) => state.clearSelectedSlot);
  const setCustomSlotDate = useQueueStore((state) => state.setCustomSlotDate);
  const setCustomSelectedDaySlot = useQueueStore(
    (state) => state.setCustomSelectedDaySlot
  );
  const customSelectedDaySlot = useQueueStore(
    (state) => state.customSelectedDaySlot
  );
  const setCustomSlotDuration = useQueueStore(
    (state) => state.setCustomSlotDuration
  );
  const customSlotDuration = useQueueStore((state) => state.customSlotDuration);

  const { data: queueConfigData } = useGetQueueConfig();

  const selectedDoctor = useCreateAptStore((state) => state.selectedDoctor);
  const canAllowSlotOverbooking =
    !!queueConfigData?.data?.apt_config?.allow_slot_overbooking;
  const slotOverbookingLimitPerDay =
    queueConfigData?.data?.apt_config?.slot_overbooking_limit_per_day || {};
  const slotOverbookingLimit =
    slotOverbookingLimitPerDay[selectedDoctor?.oid || ""] || "";
  const slotOverbookingLimitNumber = !isNaN(parseInt(slotOverbookingLimit))
    ? parseInt(slotOverbookingLimit)
    : Number.MAX_SAFE_INTEGER;

  const selectedDate = useQueueStore((state) => state.selectedDate);

  const isSlotDateFromPast = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (customSlotDate.getTime() < today.getTime()) {
      return true;
    }
    return false;
  }, [customSlotDate]);

  const [clinicIdForSlot, setClinicIdForSlot] = useState(
    aptData?.clinic_id || clinicIdFromCreateAptStore || clinicId
  );
  const { data: accessProfile } = useMyAccessProfileV2({
    clinicId: clinicIdForSlot,
  });

  const setSelectedDoctor = useCreateAptStore(
    (state) => state.setSelectedDoctor
  );

  React.useEffect(() => {
    const isAptDocPresentInClinicIndex =
      accessProfile?.doctors.findIndex((d) => d.oid === aptData?.doctor_oid) ||
      0;

    if (accessProfile?.doctors?.[isAptDocPresentInClinicIndex]) {
      setSelectedDoctor(accessProfile.doctors[isAptDocPresentInClinicIndex]);
    }
  }, [accessProfile, aptData?.doctor_oid]);

  const doctorIdForSlot = selectedDoctor?.oid;

  useEffect(() => {
    if (selectedDate < new Date() || isSameDay(selectedDate, new Date())) {
      setCustomSelectedDaySlot("today");
      setCustomSlotDate(new Date());
      return;
    }

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (isSameDay(selectedDate, tomorrow)) {
      setCustomSelectedDaySlot("tomorrow");
      setCustomSlotDate(selectedDate);
      return;
    }

    const dateOnSaturday = new Date();
    const daysFromSaturday = 6 - dateOnSaturday.getDay();
    dateOnSaturday.setDate(dateOnSaturday.getDate() + daysFromSaturday);

    if (isSameDay(selectedDate, dateOnSaturday)) {
      setCustomSelectedDaySlot("saturday");
      setCustomSlotDate(selectedDate);
      return;
    }

    setCustomSelectedDaySlot("custom");
    setCustomSlotDate(selectedDate);
  }, [selectedDate, setCustomSelectedDaySlot, setCustomSlotDate]);

  const { mutateAsync: postBookSlot } = usePostBookSlot();
  const { mutateAsync: postScheduleRequestedAppointment } =
    useScheduleRequestedAppointment();

  const handleCheckinClick = async () => {
    if (aptData?.patient_oid && clinicId && selectedSlot) {
      track(selectedSlot ? "book_appointment" : "checkin", {});
      const newDateObject = new Date();
      let channel = APPOINTMENT_CHANNEL.WALKIN;
      const docId = aptData?.doctor_oid;

      if (selectedSlot) {
        channel = getBookedAptChannel({ docIdInApt: docId });
      }

      const startTime = selectedSlot
        ? selectedSlot.startTime
        : newDateObject.toISOString();
      const endTime = selectedSlot
        ? selectedSlot.endTime
        : newDateObject.toISOString();

      const { success, data } = await postBookSlot({
        channel,
        mode,
        clinicId,
        doctorId: docId!,
        patient: {
          oid: aptData.patient_oid,
          allowMultiBooking:
            queueConfigData?.data?.apt_config?.allow_pt_multi_apt,
        },
        tags: [],
        startTime,
        endTime,
        action: selectedSlot
          ? APPOINTMENT_STATUS.BOOKED
          : APPOINTMENT_STATUS.CHECKED_IN,
      });

      if (success && data) {
        clearSelectedSlot();
        setCustomSlotDate(dayjs().toDate());
        setCustomSelectedDaySlot("today");
        setCustomSlotDuration(durationsInMinutes[2]);
        // Pass full appointment object if available, otherwise just full_date
        // Type assertion needed because FirebaseAppointment is missing some Appointment fields
        onSubmitClick?.(
          data ? (data as any as Appointment) : { full_date: startTime }
        );
      }
    }
  };
  // end //

  const isOnlyBooked = aptChannel === "BOOKED" && bookType === undefined;

  const { isFetching: isSlotsFetching, data: slotsResult } = useCustomSlots({
    docId: bookType === "RESCHEDULE" ? doctorIdForSlot : aptData?.doctor_oid,
    forceShowSlotsFromSchedule,
    clinicIdToFetch:
      bookType === "RESCHEDULE" ? clinicIdForSlot || clinicId : undefined,
  });

  const _slots = useMemo(
    () =>
      slotsResult?.slotsByDate?.[dateFormat(customSlotDate, "yyyy-mm-dd")] ??
      [],
    [slotsResult, customSlotDate]
  );

  const calendarServices = slotsResult?.services;
  const isFixedMode = getSlotsPref() === "fixed";

  const [selectedServiceConfId, setSelectedServiceConfId] = React.useState<
    string | undefined
  >();

  React.useEffect(() => {
    if (calendarServices) {
      const firstService = Object.values(calendarServices)[0];
      setSelectedServiceConfId(firstService?.conf_id ?? undefined);
    } else {
      setSelectedServiceConfId(undefined);
    }
  }, [calendarServices]);

  const slotsFilteredByService = useMemo(() => {
    if (!selectedServiceConfId || !calendarServices) return _slots;
    return _slots.filter(
      (slot) => (slot as any).conf_id === selectedServiceConfId
    );
  }, [_slots, selectedServiceConfId, calendarServices]);

  const aptToBookSlot = React.useMemo(() => aptData, [aptData]);

  const [timeOfDay, setTimeOfDay] = useState<TimeOfDayForSlots>();
  const allowedTimeOfDays = useMemo(() => {
    if (slotsFilteredByService.length) {
      const applicableTimeOfDay = getTimeOfDaysForDate(
        new Date(slotsFilteredByService[0].s)
      );
      if (applicableTimeOfDay.length === 1) {
        return [];
      }
      return applicableTimeOfDay;
    }
    return [];
  }, [slotsFilteredByService]);

  const filteredSlots = useMemo(() => {
    if (!timeOfDay) {
      return slotsFilteredByService;
    }

    const slotsToDisplay = slotsFilteredByService.filter((slot) => {
      const timeOfDayOfSlot = getTimeOfDay(new Date(slot.s));
      if (allowedTimeOfDays.includes(timeOfDay)) {
        return timeOfDayOfSlot === timeOfDay;
      }
      return true;
    });

    return slotsToDisplay;
  }, [timeOfDay, slotsFilteredByService, allowedTimeOfDays]);

  const {
    mutateAsync: postRescheduleApt,
    isLoading: isPostRescheduleAptLoading,
  } = usePostRescheduleAppointment();
  const {
    mutateAsync: postScheduleFollowup,
    isLoading: isPostScheduleFollowupLoading,
  } = useScheduleFollowupMutation();

  const { isDoctorOnly } = useDoctorStaffCheck();
  const mode = useQueueStore((state) => state.mode);

  const handleBookSlotPress = async () => {
    track("book_slot", {
      bookType,
      aptChannel,
    });

    if (
      doctorIdForSlot &&
      clinicId &&
      selectedSlot?.startTime &&
      selectedSlot.endTime &&
      aptChannel === "BOOKED" &&
      bookType === "SCHEDULE"
    ) {
      handleCheckinClick();
    }

    if (
      doctorIdForSlot &&
      clinicId &&
      aptToBookSlot?.aid &&
      selectedSlot?.startTime &&
      selectedSlot.endTime
    ) {
      if (bookType === "RESCHEDULE" && aptChannel === "BOOKED") {
        const getSlots = await getCustomSlots(
          dayjs(selectedSlot?.startTime).format("YYYY-MM-DD"),
          getMe().business.id,
          customSlotDuration,
          clinicIdForSlot || clinicId,
          doctorIdForSlot !== aptData.doctor_oid
            ? doctorIdForSlot
            : aptData.doctor_oid,
          mode,
          false
        );

        const slots =
          getSlots.slotsByDate[
            dayjs(selectedSlot?.startTime).format("YYYY-MM-DD")
          ];

        const slot = slots?.find((s) =>
          dayjs(s.s).isSame(dayjs(selectedSlot?.startTime))
        );

        console.log("debugx: slot", {
          slots,
          startTime: dayjs(selectedSlot?.startTime).format("YYYY-MM-DD"),
          slot,
        });

        if (!slot?.available) {
          notification.error({
            message:
              "This slot is not available. Please select a different slot.",
          });
          return;
        }

        const response = await postRescheduleApt({
          aid: aptToBookSlot.aid,
          clinicId: clinicIdForSlot || clinicId,
          channel: isDoctorOnly
            ? APPOINTMENT_CHANNEL.DOCTOR
            : APPOINTMENT_CHANNEL.STAFF,
          startTime: selectedSlot?.startTime,
          endTime: selectedSlot?.endTime,
          mode,
          ...(doctorIdForSlot !== aptData.doctor_oid
            ? {
                doctor_to_change_to_oid: doctorIdForSlot,
                updatedDoctorOid:
                  doctorIdForSlot !== aptData.doctor_oid
                    ? doctorIdForSlot
                    : undefined,
              }
            : {}),
        });
        if (response.data?.aid) {
          notification.success({
            message: "Appointment Rescheduled",
            placement: "bottomRight",
            description: `Your appointment with ${
              aptToBookSlot.patient.fln
            } has been rescheduled on ${dateFormat(
              selectedSlot?.startTime,
              "ddS mmmm, yyyy"
            )} at ${dateFormat(
              selectedSlot?.startTime,
              "hh:MM TT"
            )}. You can check the updated appointment details in the queue.`,
          });
          // Pass full appointment object if available, otherwise just full_date
          // response.data is the FirebaseAppointment object directly from rescheduleAppointment controller
          const appointment = response.data;
          onSubmitClick?.(
            appointment && "aid" in appointment
              ? (appointment as any as Appointment)
              : { full_date: response.data.full_date.toDate().toISOString() }
          );
        } else {
          notification.error({
            message: "Error in rescheduling appointment",
            placement: "bottomRight",
            description: "Something went wrong. Please try again.",
          });
        }
      } else if (bookType === "SCHEDULE" && aptChannel === "FOLLOW-UP") {
        const response = await postScheduleFollowup({
          appointment: aptToBookSlot,
          startTime: selectedSlot?.startTime,
          endTime: selectedSlot?.endTime,
          mode,
          action: "SCHEDULE",
        });

        if (response.success) {
          notification.success({
            placement: "bottomRight",
            message: "Follow Up Appointment Scheduled",
            description: `Your follow up appointment with ${
              aptToBookSlot.patient.fln
            } has been scheduled on ${dateFormat(
              selectedSlot?.startTime,
              "ddS mmmm, yyyy"
            )} at ${dateFormat(selectedSlot?.startTime, "hh:MM TT")}.`,
          });
          clearSelectedSlot();
          setCustomSlotDate(new Date());
          setCustomSelectedDaySlot("today");
          setCustomSlotDuration(durationsInMinutes[2]);
          onSubmitClick?.({ full_date: selectedSlot?.startTime });
        } else {
          notification.error({
            placement: "bottomRight",
            message: "Error in scheduling follow up appointment",
            description: "Something went wrong. Please try again.",
          });
        }
      } else if (bookType === "SCHEDULE" && aptChannel === "REQUESTED") {
        const response = await postScheduleRequestedAppointment({
          appointment: aptToBookSlot,
          startTime: selectedSlot?.startTime,
          endTime: selectedSlot?.endTime,
          action: "SCHEDULE",
          mode,
        });

        if (response) {
          notification.success({
            placement: "bottomRight",
            message: "Appointment Scheduled",
            description: `Your appointment with ${
              aptToBookSlot.patient.fln
            } has been scheduled on ${dateFormat(
              selectedSlot?.startTime,
              "ddS mmmm, yyyy"
            )} at ${dateFormat(selectedSlot?.startTime, "hh:MM TT")}.`,
          });

          clearSelectedSlot();
          setCustomSlotDate(new Date());
          setCustomSelectedDaySlot("today");
          setCustomSlotDuration(durationsInMinutes[2]);
          onSubmitClick?.({ full_date: selectedSlot.startTime });
        } else {
          notification.error({
            placement: "bottomRight",
            message: "Error occured while scheduling appointment.",
            description: "Something went wrong. Please try again.",
          });
        }
      } else {
        onSubmitClick?.();
      }
    } else {
      onSubmitClick?.();
    }
  };

  const handleSelectPastDate = () => {
    const slotDateObj = new Date(customSlotDate);
    slotDateObj.setHours(23, 59, 0, 0);
    setCustomSlotDuration(1);
    selectSlot({
      startTime: slotDateObj.toISOString(),
      endTime: slotDateObj.toISOString(),
    });
    onSubmitClick?.();
  };

  // const disabledDate = (current: dayjs.Dayjs) => {
  //   const isPast = current < dayjs().startOf("day");
  //   return isPast;
  // };

  const [showDatePicker, setShowDatePicker] = React.useState<boolean>(false);

  const { clinics, selectedClinicName } = useMemo(() => {
    const clinics = accessProfile?.clinics || [];

    return {
      clinics,
      selectedClinicName:
        clinics.find((item) => item.id === clinicIdForSlot)?.name || "",
    };
  }, [clinicIdForSlot, accessProfile?.clinics]);

  return (
    <>
      <div className="dr-flex dr-flex-col dr-flex-1">
        <SelectMode />
        {clinics.length > 1 &&
        bookType === "RESCHEDULE" &&
        aptChannel === "BOOKED" ? (
          <>
            <div className="dr-px-16 dr-py-16 dr-flex dr-flex-col dr-space-y-16">
              <p>Select Clinic</p>
              <InputField
                suggestions={clinics.map((item) => ({
                  id: item.id,
                  name: item.name,
                }))}
                value={selectedClinicName}
                onSuggestionSelect={({ id }): void => {
                  clearSelectedSlot();
                  setClinicIdForSlot(id);
                }}
                customSuffix={
                  <div className="dr-h-full dr-flex dr-items-center dr-justify-center dr-px-12 dr-rounded-r-8">
                    <ChevronDownRegularIcon className="dr-w-14 dr-h-14 dr-text-darwin-touch-neutral-400" />
                  </div>
                }
                customPrefix={
                  <div className="dr-h-full dr-flex dr-items-center dr-justify-center dr-pl-12 dr-rounded-r-8">
                    <HouseMedicalChimneyRegularIcon className="dr-w-16 dr-h-16" />
                  </div>
                }
              />
            </div>
            <div className="dr-w-full dr-h-1 dr-bg-doctor-darwin-neutral-100" />
          </>
        ) : null}
        {bookType === "RESCHEDULE" &&
        aptChannel === "BOOKED" &&
        // doctorIdForSlot &&
        !!accessProfile?.doctors?.length &&
        accessProfile.doctors.length > 1 ? (
          <SelectDoctorForSlot
            clinicId={clinicIdForSlot}
            selectedDoctorId={doctorIdForSlot}
            setSelectedDoctorId={(docId) => {
              setSelectedDoctor(
                accessProfile?.doctors?.find((item) => item.oid === docId)
              );
              clearSelectedSlot();
            }}
          />
        ) : null}
        <Section title="Select Date" outerClassname="dr-px-16">
          <Radio.Group
            onChange={(e) => {
              track("select_date", {
                date: e.target.value,
              });
              setCustomSelectedDaySlot(e.target.value);

              switch (e.target.value) {
                case "today":
                  setCustomSlotDate(new Date());
                  break;
                case "tomorrow":
                  setCustomSlotDate(
                    dayjs().add(1, "day").startOf("day").toDate()
                  );
                  break;
                case "saturday":
                  setCustomSlotDate(dayjs().day(6).startOf("day").toDate());
                  break;
                case "custom":
                  break;
              }

              clearSelectedSlot();
            }}
            value={customSelectedDaySlot}
            optionType="button"
            buttonStyle="solid"
            className="dr-space-x-12 flex items-center"
          >
            <SelectSlotChip value={"today"}>Today</SelectSlotChip>
            <SelectSlotChip value={"tomorrow"}>Tomorrow</SelectSlotChip>
            {dayjs(Date.now()).get("day") < 5 ? (
              <SelectSlotChip value={"saturday"}>
                {dayjs(Date.now()).day(6).format("dddd")}
              </SelectSlotChip>
            ) : (
              <></>
            )}
            <div className="dr-relative">
              <SelectSlotChip
                value={"custom"}
                onClick={() => {
                  setShowDatePicker(true);
                }}
              >
                <div className="BodyBody2Regular flex items-center min-w-max space-x-12 focus-within:dr-text-doctor-darwin-neutral-0">
                  <div>
                    {customSelectedDaySlot === "custom"
                      ? dayjs(new Date(customSlotDate)).format("DD' MMM")
                      : "Custom"}
                  </div>
                  <CalendarSolidIcon className="w-14" />
                </div>
              </SelectSlotChip>
              <DatePicker
                style={{ padding: 0, zIndex: 1 }}
                value={customSlotDate ? dayjs(new Date(customSlotDate)) : null}
                open={showDatePicker}
                onChange={(e) => {
                  track("select_date_custom", {
                    date: e?.format("YYYY-MM-DD"),
                  });
                  setCustomSlotDate(
                    e?.startOf("day")?.toDate() || dayjs().toDate()
                  );
                  setShowDatePicker(false);
                }}
                onOpenChange={(openState) => {
                  if (!openState) {
                    setShowDatePicker(false);
                  }
                }}
                className="dr-absolute dr-left-0"
                inputRender={() => <></>}
                suffixIcon={null}
                placeholder="Custom"
                bordered={false}
                clearIcon={false}
              />
            </div>
          </Radio.Group>
        </Section>
        {isSlotDateFromPast ? (
          <PastVisitSlotPlaceholderUi />
        ) : (
          <>
            <div className="dr-w-full dr-h-1 dr-bg-doctor-darwin-neutral-100" />
            {!isFixedMode && <VisitDuration />}
            {!isFixedMode && (
              <div className="dr-w-full dr-h-1 dr-bg-doctor-darwin-neutral-100" />
            )}
            {isFixedMode && (
              <>
                {isSlotsFetching ? null : calendarServices &&
                  Object.keys(calendarServices).length > 0 ? (
                  <>
                    <style>
                      {`
                         

                          .custom-full-width-ant-wrapper-select .ant-select-selector {
                            min-width: 100% !important;
                          }
                      `}
                    </style>

                    <div className="dr-px-16 dr-py-16 dr-flex dr-items-center dr-gap-x-16 dr-w-full">
                      <span className="BodyBody2Bold dr-whitespace-nowrap">
                        Services
                      </span>

                      <Select
                        placeholder="Select a service"
                        value={selectedServiceConfId}
                        onChange={setSelectedServiceConfId}
                        dropdownMatchSelectWidth={false}
                        className="custom-full-width-ant-wrapper-select dr-h-36 ant-selector-transparent BodyBody2Regular dr-rounded-8 dr-cursor-pointer dr-flex-1"
                        options={Object.values(calendarServices).map((svc) => ({
                          label: formatServiceDuration(
                            svc.service_name,
                            svc.duration
                          ),
                          value: svc.conf_id,
                        }))}
                      />
                    </div>

                    <div className="dr-w-full dr-h-1 dr-bg-doctor-darwin-neutral-100" />
                  </>
                ) : (
                  <div className="dr-px-24 dr-py-32 dr-flex dr-flex-col dr-items-center dr-justify-center dr-text-center dr-gap-y-12">
                    <div className="dr-w-48 dr-h-48 dr-rounded-full dr-bg-darwin-primary-bg-light dr-flex dr-items-center dr-justify-center">
                      <CalendarSolidIcon className="dr-w-20 dr-h-20 dr-text-darwin-touch-primary-primary" />
                    </div>
                    <div className="dr-flex dr-flex-col dr-gap-y-4">
                      <p className="BodyBody2Bold dr-text-darwin-touch-neutral-900">
                        Calendar not set up
                      </p>
                      <p className="Caption1Regular dr-text-darwin-touch-neutral-400">
                        Set up your schedule to use fixed time slots
                      </p>
                    </div>
                    <a
                      href="/app/calendar"
                      className="dr-mt-4 dr-inline-flex dr-items-center dr-px-16 dr-py-8 dr-rounded-8 dr-border dr-border-darwin-touch-primary-primary dr-text-darwin-touch-primary-primary BodyBody2Regular dr-no-underline hover:dr-bg-darwin-primary-bg-light dr-transition-colors"
                    >
                      Set up calendar
                    </a>
                  </div>
                )}
              </>
            )}
            {(!isFixedMode || !!selectedServiceConfId) && (
              <Section
                loading={isSlotsFetching}
                title="Select Slot"
                outerClassname="dr-px-16"
                sideComponent={
                  <div className="flex items-center dr-space-x-16">
                    {allowedTimeOfDays?.map((tod) => {
                      const Icon =
                        tod === "morning"
                          ? SunriseSolidIcon
                          : tod === "afternoon"
                            ? SunSolidIcon
                            : MoonSolidIcon;
                      return (
                        <Icon
                          key={tod}
                          onClick={() => {
                            setTimeOfDay((prev) =>
                              prev === tod ? undefined : tod
                            );
                          }}
                          className={`w-16 h-16 dr-cursor-pointer ${
                            timeOfDay === tod
                              ? "dr-text-darwin-touch-primary-primary"
                              : "dr-text-doctor-darwin-neutral-400"
                          } `}
                        />
                      );
                    })}
                  </div>
                }
              >
                {filteredSlots && filteredSlots.length > 0 ? (
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    value={JSON.stringify(selectedSlot)}
                    className="grid dr-grid-cols-3 dr-gap-16"
                  >
                    {filteredSlots.map((slot) => (
                      <SelectSlotChip
                        key={`${slot.s}_${slot.e}`}
                        value={JSON.stringify({
                          startTime: slot.s,
                          endTime: slot.e,
                        })}
                        isDisabled={
                          slot.is_calendar_blocked
                            ? true
                            : !canAllowSlotOverbooking
                              ? !slot.available
                              : slot.slots_booked >= slotOverbookingLimitNumber
                                ? true
                                : false
                        }
                        className={canAllowSlotOverbooking ? "!dr-h-44" : ""}
                        onClick={(e) => {
                          const slot = JSON.parse(
                            (e.target as HTMLInputElement).value
                          );
                          if (
                            selectedSlot?.startTime === slot.startTime &&
                            selectedSlot?.endTime === slot.endTime
                          ) {
                            clearSelectedSlot();
                          } else {
                            selectSlot(slot);
                          }
                        }}
                      >
                        <div className="dr-flex dr-flex-col dr-space-y-2 dr-items-center dr-justify-center">
                          <p>{dateFormat(slot.s, "hh:MM TT")}</p>
                          {canAllowSlotOverbooking &&
                          slot.slots_booked > 0 &&
                          !slot.is_calendar_blocked ? (
                            <p className="Caption1Medium dr-capitalize">
                              {slot.slots_booked} BOOKING
                            </p>
                          ) : null}
                        </div>
                      </SelectSlotChip>
                    ))}
                  </Radio.Group>
                ) : (
                  <p className="BodyBody2Regular">No slots available</p>
                )}
              </Section>
            )}
          </>
        )}
      </div>
      <div className="w-full p-16  dr-rounded-t-16 sticky bottom-0 dr-bg-darwin-touch-neutral-0 dr-drop-shadow-6 dr-z-1000">
        {isSlotDateFromPast ? (
          <PrimaryButton
            title="Select Date"
            extraClass="dr-w-full"
            onClick={handleSelectPastDate}
          />
        ) : (
          <Button
            onClick={handleBookSlotPress}
            loading={
              isPostRescheduleAptLoading || isPostScheduleFollowupLoading
            }
            disabled={selectedSlot === undefined}
            className="flex items-center justify-center w-full rounded-8 focus:!dr-text-doctor-darwin-neutral-0  active:!dr-text-doctor-darwin-neutral-0  hover:!dr-text-doctor-darwin-neutral-0 !dr-text-doctor-darwin-neutral-0 !dr-bg-darwin-touch-primary-primary hover:!dr-bg-darwin-touch-primary-dark hover:!dr-border-darwin-touch-primary-dark focus:!dr-bg-darwin-touch-primary-dark focus:!dr-border-darwin-touch-primary-dark active:!dr-bg-darwin-touch-primary-dark active:!dr-border-darwin-touch-primary-dark border !dr-border-darwin-touch-primary-primary disabled:!dr-bg-darwin-touch-neutral-300 !BodyBody2Regular !dr-h-40 solid-button"
          >
            {isOnlyBooked
              ? "Select Slot"
              : bookType === "RESCHEDULE"
                ? "Reschedule Appointment"
                : "Book Slot"}
          </Button>
        )}

        <style>{`
          .ant-btn[disabled].solid-button {
            background-color: #BABABA !important;
            border-color: #BABABA !important;
            color: #FFFFFF !important;
          }
        `}</style>
      </div>
      <style>{`
            .ant-radio-button-wrapper:before {
                display: none !important;
            }
            .ant-radio-button-checked {
              background: #215FFF !important;
              border-radius: 0.5rem !important;
              color: white !important;
            }
            label.ant-radio-button-wrapper:hover {
              color: #215FFF !important;
            }
            .ant-radio-group-solid .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover {
              color: white !important;
            }
          `}</style>
    </>
  );
};

export const Section = ({
  title,
  children,
  sideComponent,
  loading = false,
  outerClassname,
  titleClassname,
}: PropsWithChildren<{
  title: string;
  sideComponent?: JSX.Element;
  loading?: boolean;
  outerClassname?: string;
  titleClassname?: string;
}>) => {
  return (
    <div className={`dr-py-16 ${outerClassname || ""}`}>
      <div
        className={`dr-flex dr-items-center dr-justify-between BodyBody2Bold dr-pb-16 ${
          titleClassname || ""
        }`}
      >
        <div>{title}</div>
        {sideComponent ? sideComponent : <></>}
      </div>
      {loading ? (
        <div className="flex flex-col dr-space-y-14 items-center justify-center h-150">
          <div className="w-74 h-74 rounded-full dr-bg-doctor-darwin-accent-symptoms-blue-200 flex dr-items-center justify-center">
            <div className="dot-typing"></div>
          </div>
          <div className="BodyBody1Regular dr-text-darwin-touch-neutral-1000">
            loading available slots...
          </div>
        </div>
      ) : (
        <div className="dr-overflow-auto">{children}</div>
      )}
    </div>
  );
};
